import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import ProductCard from '../components/ProductCard';
import { getCmsCollection, getCmsFrontpageSections, getCmsSettings } from '../lib/cms';
import { imageUrl } from '../lib/images';

const HERO_DOG_IMG = 'https://assets.auxodev.dk/assets/e1e86ea9-8aac-4deb-bab2-e96b6ba9dd16/hund.jpg';
const FIBERKOST_IMG = 'https://assets.auxodev.dk/assets/e1e86ea9-8aac-4deb-bab2-e96b6ba9dd16/fiberkost.png';
const UDSTILLING_IMG = 'https://assets.auxodev.dk/assets/e1e86ea9-8aac-4deb-bab2-e96b6ba9dd16/csm-udstilling-790f2eca8e.jpg';
const BILER_IMG = 'https://assets.auxodev.dk/assets/e1e86ea9-8aac-4deb-bab2-e96b6ba9dd16/csm-img-20200217-121448-d9821f947e.jpg';
const STAND_IMG = 'https://assets.auxodev.dk/assets/e1e86ea9-8aac-4deb-bab2-e96b6ba9dd16/csm-img-20200207-135331-0ad58555a7.jpg';

const DOG_NEEDS = [
  {
    behov: 'hvalp',
    title: 'Hvalp (2-20 mdr)',
    subtitle: 'KROMIX Junior & Minikost',
    icon: 'fa-light fa-dog',
  },
  {
    behov: 'aktiv',
    title: 'Aktiv & Sport',
    subtitle: 'KROMIX Fiberkost & Favorit',
    icon: 'fa-light fa-bolt',
  },
  {
    behov: 'sensitiv',
    title: 'Sensitiv / Sart mave',
    subtitle: 'KROMIX Lam & Ris',
    icon: 'fa-light fa-shield-heart',
  },
  {
    behov: 'smaa-hunde',
    title: 'Små racer / Mini',
    subtitle: 'KROMIX Minies',
    icon: 'fa-light fa-bone',
  },
  {
    behov: 'alle-racer',
    title: 'Alle racer / Voksen',
    subtitle: 'KROMIX Naturkost',
    icon: 'fa-light fa-wheat-awn',
  },
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [cmsSections, setCmsSections] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [prodRes, sections, globalSettings] = await Promise.all([
          getCmsCollection('produkter', {
            kategori: 'hund',
            limit: 6,
            sort: 'sorteringsorden',
            order: 'asc',
          }),
          getCmsFrontpageSections(),
          getCmsSettings(),
        ]);

        if (isMounted) {
          setFeaturedProducts(prodRes.items || []);
          if (sections && sections.length > 0) setCmsSections(sections);
          if (globalSettings) setSettings(globalSettings);
          setLoading(false);
        }
      } catch (err) {
        console.error('Fejl ved indlæsning af forside data:', err);
        if (isMounted) setLoading(false);
      }
    }
    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  const heroSec = cmsSections.find((s) => s.sektion_id === 'hero');
  const behovSec = cmsSections.find((s) => s.sektion_id === 'behov');
  const udvalgtSec = cmsSections.find((s) => s.sektion_id === 'udvalgte_produkter');
  const raavarerSec = cmsSections.find((s) => s.sektion_id === 'raavarer_historie');
  const joseraSec = cmsSections.find((s) => s.sektion_id === 'josera');
  const socialSec = cmsSections.find((s) => s.sektion_id === 'social');

  const telefon = settings?.telefon || '75 73 18 61';
  const heroDogImg = heroSec?.primaert_billede?.[0] || HERO_DOG_IMG;
  const heroBgImg = heroSec?.sekundaert_billede?.[0] || 'https://assets.auxodev.dk/assets/e1e86ea9-8aac-4deb-bab2-e96b6ba9dd16/imgi-5-baggrund.jpg';
  const raavarerImg = raavarerSec?.primaert_billede?.[0] || UDSTILLING_IMG;
  const socialImg1 = socialSec?.primaert_billede?.[0] || UDSTILLING_IMG;
  const socialImg2 = socialSec?.sekundaert_billede?.[0] || STAND_IMG;
  const socialImg3 = BILER_IMG;

  return (
    <>
      <SeoHead
        title="KROMIX A/S | Dansk kvalitetsfoder til hunde & katte"
        description="Vi skaffer resultater. Rigtig ernæring - sund levevis. Dansk kvalitetsfoder til aktive og sensitive hunde siden 1986."
      />

      {/* Hero Section: Deep Moss Nordic statement section */}
      <section className="relative bg-[#39463A] text-[#FCFAF5] overflow-hidden">
        {/* Background image with transparent dark overlay */}
        <div
          className="absolute inset-0 z-0 bg-fixed bg-cover bg-center"
          style={{
            backgroundImage: `url(${imageUrl(heroBgImg, 'width=1920,quality=85,format=auto')})`
          }}
        >
          {/* More transparent overlay for a warm natural look */}
          <div className="absolute inset-0 bg-[#273228]/35 backdrop-blur-[0.5px]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#273228]/80 via-[#39463A]/50 to-[#273228]/45" />
        </div>

        {/* Subtle decorative background pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(#FCFAF5_1px,transparent_1px)] [background-size:20px_20px] z-0" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-stretch">
            {/* Left Content Column */}
            <div className="lg:col-span-7 flex flex-col justify-center space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#273228]/90 text-[#DED3C0] text-xs font-bold uppercase tracking-wider border border-[#71806A]/40 w-fit backdrop-blur-xs">
                <i className="fa-solid fa-award text-[#B98262]" aria-hidden="true" />
                <span>{heroSec?.badge || 'Dansk foderkvalitet siden 1986'}</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#FCFAF5] leading-tight">
                {heroSec?.overskrift || 'Vi skaffer resultater'}
                <span className="block text-[#DED3C0] text-2xl sm:text-3xl lg:text-4xl font-normal mt-2">
                  {heroSec?.underoverskrift || 'Rigtig ernæring – sund levevis'}
                </span>
              </h1>

              {heroSec?.broedtekst ? (
                <div
                  className="text-base sm:text-lg text-[#FCFAF5]/95 max-w-2xl leading-relaxed [&>p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: heroSec.broedtekst }}
                />
              ) : (
                <p className="text-base sm:text-lg text-[#FCFAF5]/95 max-w-2xl leading-relaxed">
                  KROMIX tilbyder specialiseret hundefoder udviklet af fagfolk med fokus på råvarer af højeste kvalitet, høj fordøjelighed og optimal trivsel for aktive hunde, familiehunde og hvalpe.
                </p>
              )}

              {/* CTAs: Sage Olive for primary CTA */}
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to={heroSec?.knap_link || '/hundefoder'}
                  className="px-6 py-3.5 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-[#FCFAF5] font-bold text-sm uppercase tracking-wider transition-all shadow-md hover:shadow-lg active:scale-98 flex items-center gap-2"
                >
                  <span>{heroSec?.knap_tekst || 'Se hundefoder'}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to={heroSec?.sekundaer_knap_link || '/kontakt'}
                  className="px-6 py-3.5 rounded-xl bg-[#FCFAF5] hover:bg-white text-[#39463A] font-bold text-sm uppercase tracking-wider transition-all shadow-sm hover:shadow-md active:scale-98 flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope text-[#71806A]" aria-hidden="true" />
                  <span>{heroSec?.sekundaer_knap_tekst || 'Kontakt os'}</span>
                </Link>
              </div>

              {/* Direct call note */}
              <p className="text-xs text-[#DED3C0] pt-2 flex items-center gap-2">
                <i className="fa-solid fa-circle-info text-[#BCC6B2]" aria-hidden="true" />
                <span>Har du spørgsmål? Ring til os på <a href={`tel:${telefon.replace(/\s+/g, '')}`} className="text-white font-bold underline hover:text-[#DED3C0] whitespace-nowrap">{telefon}</a></span>
              </p>
            </div>

            {/* Right Hero Visual Column: Same height as left content column */}
            <div className="lg:col-span-5 flex items-stretch">
              <div className="relative w-full h-full min-h-[380px] lg:min-h-[460px] rounded-3xl overflow-hidden shadow-2xl border-4 border-[#71806A]/30 bg-[#273228]">
                <img
                  src={imageUrl(heroDogImg, 'width=900,quality=90,format=auto')}
                  alt="KROMIX hund"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#273228]/70 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Entry: Find Foder efter Behov */}
      <section className="py-16 sm:py-20 bg-[#F4EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
              {behovSec?.badge || 'Tilpasset ernæring'}
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#39463A] mt-1">
              {behovSec?.overskrift || 'Find foder efter hundens behov'}
            </h2>
            <p className="text-sm text-[#39463A]/80 mt-2">
              {behovSec?.underoverskrift || 'KROMIX har udviklet foderblandinger tilpasset hundens alder, aktivitetsniveau og særlige behov.'}
            </p>
          </div>

          <div className="grid grid-cols-12 gap-4">
            {DOG_NEEDS.map((item, idx) => {
              const total = DOG_NEEDS.length;
              // Rule: if there is one box in last row make it fill col-12, if there are 2 boxes in last row make them fill col-6
              // Tablet (sm): 2 columns
              const smRemainder = total % 2;
              const isSmLastSingle = smRemainder === 1 && idx === total - 1;
              const smSpan = isSmLastSingle ? 'sm:col-span-12' : 'sm:col-span-6';

              // Desktop (lg): 3 columns
              const lgRemainder = total % 3;
              let lgSpan = 'lg:col-span-4';
              if (lgRemainder === 1 && idx === total - 1) {
                // 1 box in last row fills col-12
                lgSpan = 'lg:col-span-12';
              } else if (lgRemainder === 2 && idx >= total - 2) {
                // 2 boxes in last row fill col-6 each
                lgSpan = 'lg:col-span-6';
              }

              return (
                <Link
                  key={item.behov}
                  to={`/hundefoder?behov=${item.behov}`}
                  className={`col-span-12 ${smSpan} ${lgSpan} group p-6 rounded-2xl bg-[#FCFAF5] border border-[#DED3C0]/70 hover:border-[#71806A] hover:shadow-lg transition-all text-center flex flex-col items-center justify-between`}
                >
                  <div className="text-4xl sm:text-5xl text-[#71806A] group-hover:text-[#39463A] flex items-center justify-center transition-colors mb-4 pt-2">
                    <i className={item.icon} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-sm text-[#39463A] group-hover:text-[#71806A] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-[11px] text-[#39463A]/75 mt-1">
                    {item.subtitle}
                  </p>
                  <span className="mt-4 text-[10px] font-bold uppercase tracking-wider text-[#71806A] group-hover:text-[#39463A] flex items-center gap-1 transition-colors">
                    <span>Vis foder</span>
                    <i className="fa-light fa-chevron-right text-[10px]" aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products Showcase: Soft Ivory / Warm White surface */}
      <section className="py-16 sm:py-20 bg-[#FCFAF5] border-t border-b border-[#DED3C0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                {udvalgtSec?.badge || 'Produktsortiment'}
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#39463A] mt-1">
                {udvalgtSec?.overskrift || 'Udvalgt KROMIX Hundefoder'}
              </h2>
              <p className="text-sm text-[#39463A]/80 mt-1">
                {udvalgtSec?.underoverskrift || 'Velsmagende foder fyldt med energi, fremstillet af nøje udvalgte kvalitetsråvarer.'}
              </p>
            </div>
            <Link
              to={udvalgtSec?.knap_link || '/hundefoder'}
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#71806A] hover:text-[#39463A] transition-colors"
            >
              <span>{udvalgtSec?.knap_tekst || 'Se alle hundefoderprodukter'}</span>
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </Link>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="py-16 flex justify-center items-center text-sm text-[#39463A]/80">
              <i className="fa-solid fa-spinner fa-spin mr-2 text-lg text-[#71806A]" /> Indlæser produkter...
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Editorial Section: Kvalitet & Faglighed */}
      <section className="py-16 sm:py-20 bg-[#F4EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image side */}
            <div className="lg:col-span-5 relative">
              <div className="rounded-3xl overflow-hidden shadow-xl border border-[#DED3C0]/80">
                <img
                  src={imageUrl(raavarerImg, 'width=800,quality=85,format=auto')}
                  alt="KROMIX på Herning udstillingen"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-[#39463A] text-[#FCFAF5] p-5 rounded-2xl shadow-xl max-w-xs border border-[#71806A]/30">
                <p className="text-xs text-[#DED3C0] uppercase font-bold tracking-widest">
                  {raavarerSec?.faktaboks_titel || 'Foderhygiejnekontrol'}
                </p>
                <p className="text-xs mt-1 text-[#FCFAF5] font-medium leading-relaxed">
                  {raavarerSec?.faktaboks_tekst || 'Tilknyttet Fødevarestyrelsen, som foretager stikprøver til analyse for at sikre den bedste kvalitet.'}
                </p>
              </div>
            </div>

            {/* Text side */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                {raavarerSec?.badge || 'Historien & Råvarerne'}
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#39463A] leading-tight">
                {raavarerSec?.overskrift || 'Råvarer i høj kvalitet uden aromaer eller farvestoffer'}
              </h2>

              {raavarerSec?.broedtekst ? (
                <div
                  className="space-y-4 text-sm text-[#39463A]/80 leading-relaxed [&>p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: raavarerSec.broedtekst }}
                />
              ) : (
                <div className="space-y-4 text-sm text-[#39463A]/80 leading-relaxed">
                  <p>
                    Kromix blev stiftet af Henning Kromann i 1986 der igennem hele sit liv havde arbejdet med jagthunde hvor han både har været dommer ved udstillinger og markprøver.
                  </p>
                  <p>
                    I hans arbejde med jagthunde kom han til den realation at der ikke var et fodder på markedet der dækkede hans behov og der blev ideen til Kromix født, et godt kvalitetsfoder til en fair pris.
                  </p>
                  <p>
                    Kromix har råvare i fokus dette betyder hermed at vi kun anvender råvare af højkvalitet så vi dermed sikre os høj optagelse af ernæring, der tilsættes ingen aromaer, smags eller farvestofffer da kvalitet er i prioitet.
                  </p>
                </div>
              )}

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to={raavarerSec?.knap_link || '/om-kromix'}
                  className="px-5 py-3 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-[#FCFAF5] text-xs font-bold uppercase tracking-wider transition-colors shadow-xs"
                >
                  {raavarerSec?.knap_tekst || 'Læs hele historien'}
                </Link>
                <Link
                  to={raavarerSec?.sekundaer_knap_link || '/kontakt'}
                  className="px-5 py-3 rounded-xl bg-[#FCFAF5] hover:bg-white border border-[#DED3C0] text-[#39463A] text-xs font-bold uppercase tracking-wider transition-colors shadow-2xs"
                >
                  {raavarerSec?.sekundaer_knap_tekst || 'Kontakt os'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Josera Section: Deep Moss Nordic statement section */}
      <section className="py-14 bg-[#39463A] text-[#FCFAF5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#273228]/85 rounded-3xl p-8 sm:p-12 border border-[#71806A]/35 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-center lg:text-left max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-widest text-[#DED3C0]">
                {joseraSec?.badge || 'Dansk samarbejde & distribution'}
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#FCFAF5]">
                {joseraSec?.overskrift || 'Josera Premium Foder & Tilbehør'}
              </h2>
              {joseraSec?.broedtekst ? (
                <div
                  className="text-sm text-[#DED3C0]/90 leading-relaxed [&>p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: joseraSec.broedtekst }}
                />
              ) : (
                <p className="text-sm text-[#DED3C0]/90 leading-relaxed">
                  KROMIX distribuerer det anerkendte tyske Josera premium foder til hunde og katte i Danmark. Besøg vores dedikerede Josera-sektion for information om sortimentet.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 shrink-0">
              <Link
                to={joseraSec?.knap_link || '/josera'}
                className="px-6 py-3.5 rounded-xl bg-[#FCFAF5] hover:bg-white text-[#39463A] text-xs font-bold uppercase tracking-wider transition-all shadow-md active:scale-98"
              >
                {joseraSec?.knap_tekst || 'Læs om Josera'}
              </Link>
              <a
                href={joseraSec?.sekundaer_knap_link || 'https://josera.com/da'}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3.5 rounded-xl bg-transparent hover:bg-white/10 text-[#FCFAF5] border border-[#DED3C0]/60 text-xs font-bold uppercase tracking-wider transition-all inline-flex items-center gap-2"
              >
                <span>{joseraSec?.sekundaer_knap_tekst || 'Josera.com'}</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media & Facebook Section */}
      <section className="py-16 sm:py-20 bg-[#F4EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                {socialSec?.badge || 'Aktuelt & Aktiviteter'}
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#39463A] mt-1">
                {socialSec?.overskrift || 'Følg KROMIX på Facebook'}
              </h2>
              {socialSec?.broedtekst ? (
                <div
                  className="text-sm text-[#39463A]/80 mt-1 [&>p]:mb-0"
                  dangerouslySetInnerHTML={{ __html: socialSec.broedtekst }}
                />
              ) : (
                <p className="text-sm text-[#39463A]/80 mt-1">
                  Følg med i vores arrangementer, messer, nyheder og aktiviteter på de danske landeveje.
                </p>
              )}
            </div>
            <a
              href={socialSec?.knap_link || 'https://www.facebook.com/KROMIX'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white text-xs font-bold transition-all shadow-md active:scale-98"
              aria-label="Følg KROMIX på Facebook (åbner i nyt vindue)"
            >
              <i className="fa-brands fa-facebook text-base text-[#FCFAF5]" aria-hidden="true" />
              <span>{socialSec?.knap_tekst || 'Følg KROMIX på Facebook'}</span>
            </a>
          </div>

          {/* Social posts visual grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#FCFAF5] rounded-2xl border border-[#DED3C0] overflow-hidden shadow-xs hover:shadow-md transition-shadow">
              <img
                src={imageUrl(socialImg1, 'width=600,quality=85,format=auto')}
                alt="Herning udstillingen"
                className="w-full h-48 object-cover"
                loading="lazy"
              />
              <div className="p-5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#71806A]">Arrangement</span>
                <h4 className="font-bold text-sm text-[#39463A] mt-1">Herning Udstillingen</h4>
                <p className="text-xs text-[#39463A]/80 mt-2 leading-relaxed">
                  Så er vi klar til at præsentere vores nye foder på Herning udstillingen 2019, og sikken en fantastisk respons vi fik!
                </p>
              </div>
            </div>

            <div className="bg-[#FCFAF5] rounded-2xl border border-[#DED3C0] overflow-hidden shadow-xs hover:shadow-md transition-shadow">
              <img
                src={imageUrl(socialImg2, 'width=600,quality=85,format=auto')}
                alt="Ny messestand"
                className="w-full h-48 object-cover"
                loading="lazy"
              />
              <div className="p-5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#71806A]">Messe & Udstilling</span>
                <h4 className="font-bold text-sm text-[#39463A] mt-1">Ny KROMIX Messestand</h4>
                <p className="text-xs text-[#39463A]/80 mt-2 leading-relaxed">
                  Så er vi klar til at tage imod gæster med vores nye messestand.
                </p>
              </div>
            </div>

            <div className="bg-[#FCFAF5] rounded-2xl border border-[#DED3C0] overflow-hidden shadow-xs hover:shadow-md transition-shadow">
              <img
                src={imageUrl(socialImg3, 'width=600,quality=85,format=auto')}
                alt="KROMIX biler"
                className="w-full h-48 object-cover"
                loading="lazy"
              />
              <div className="p-5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#71806A]">Distribution</span>
                <h4 className="font-bold text-sm text-[#39463A] mt-1">På de danske landeveje</h4>
                <p className="text-xs text-[#39463A]/80 mt-2 leading-relaxed">
                  Så er de nydekorerede biler klar til at komme ud og køre på de danske landeveje.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
